import {
    PackageSubscription,
    RateLimit,
    RevenueSummary,
    RolePermissionData,
    Tenant,
    TenantCompactListItem,
    TenantCreateDto,
    TenantFullListItem,
    TenantInvoice,
    TenantInvoiceFilter,
    TenantInvoiceGenerationResult,
} from "../../../features/tenant/tenant.types";
import { TenantUser } from "../../../features/users/users.types";
import { smePlusApi } from "../index";
import { EntityCreateResult, EntityUpdateResult, IDictionary, ListItem, PagedResult } from "../../types";

const basePath = "tenants";

export const tenantApi = smePlusApi.injectEndpoints({
    endpoints: (builder) => ({
        getTenants: builder.query<Tenant[], void>({
            query: () => ({
                url: `${basePath}/CurrentCompany/All`,
                method: "GET",
            }),
            providesTags: () => [{ type: "Tenant", id: "LIST" }],
        }),
        getAllTenants: builder.query<TenantFullListItem[], void>({
            query: () => ({
                url: `${basePath}/All`,
                method: "GET",
            }),
            providesTags: () => [{ type: "Tenant", id: "LIST" }],
            keepUnusedDataFor: 300, // 5 minutes
        }),
        getSubscribedTenants: builder.query<TenantCompactListItem[], void>({
            query: () => ({
                url: `${basePath}/Subscribed`,
                method: "GET",
            }),
            providesTags: () => [{ type: "Tenant", id: "SUBSCRIBED" }],
            keepUnusedDataFor: 3600, // 1 hour
        }),
        getCurrentTenant: builder.query<Tenant, void>({
            query: () => ({
                url: `${basePath}/Current`,
                method: "GET",
            }),
            providesTags: () => [{ type: "Tenant", id: "CURRENT" }],
            keepUnusedDataFor: 300, // 5 minutes
        }),
        getTenant: builder.query<Tenant, string>({
            query: (id) => ({
                url: `${basePath}/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "Tenant", id }],
        }),
        createTenant: builder.mutation<EntityCreateResult, TenantCreateDto>({
            query: (tenant) => ({
                url: basePath,
                method: "POST",
                data: tenant,
            }),
            invalidatesTags: () => [
                { type: "Tenant", id: "LIST" },
                { type: "Tenant", id: "SUBSCRIBED" },
            ],
        }),
        updateTenant: builder.mutation<EntityUpdateResult, { id: string; tenant: TenantCreateDto }>({
            query: ({ id, tenant }) => ({
                url: `${basePath}/${id}`,
                method: "PUT",
                data: tenant,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: "Tenant", id },
                { type: "Tenant", id: "LIST" },
                { type: "Config", tenantId: id },
                { type: "Tenant", id: "SUBSCRIBED" },
            ],
        }),
        updateCurrentTenant: builder.mutation<EntityUpdateResult, TenantCreateDto>({
            query: (tenant) => ({
                url: `${basePath}/Current`,
                method: "PUT",
                data: tenant,
            }),
            invalidatesTags: () => [
                { type: "Tenant", id: "CURRENT" },
                { type: "Tenant", id: "SUBSCRIBED" },
            ],
        }),
        deleteTenant: builder.mutation<void, string>({
            query: (id) => ({
                url: `${basePath}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [
                { type: "Tenant", id: "LIST" },
                { type: "Tenant", id: "SUBSCRIBED" },
            ],
        }),
        getTenantUsers: builder.query<TenantUser[], string>({
            query: (id) => ({
                url: `${basePath}/${id}/Users`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "TenantUsers", id }],
            keepUnusedDataFor: 300,
        }),
        setPrimaryContact: builder.mutation<void, { tenantId: string; userId: string }>({
            query: ({ tenantId, userId }) => ({
                url: `${basePath}/${tenantId}/PrimaryContact/${userId}`,
                method: "PUT",
                data: {},
            }),
            invalidatesTags: (result, error, { tenantId }) => [
                { type: "TenantUsers", id: tenantId },
                { type: "Tenant", id: "LIST" },
            ],
        }),
        getTenantFeatures: builder.query<string[], string>({
            query: (id) => ({
                url: `${basePath}/${id}/Features`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "TenantFeatures", id }],
            keepUnusedDataFor: 300,
        }),
        updateTenantFeatures: builder.mutation<EntityUpdateResult, { id: string; features: string[] }>({
            query: ({ id, features }) => ({
                url: `${basePath}/${id}/Features`,
                method: "PUT",
                data: features,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: "TenantFeatures", id },
                { type: "Config", tenantId: id },
            ],
        }),
        getNatureOfBusinesses: builder.query<ListItem[], void>({
            query: () => ({
                url: `${basePath}/NatureOfBusinesses`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600, // 1 hour
            providesTags: ["NatureOfBusinesses"],
        }),
        getAllFeatures: builder.query<IDictionary<ListItem[]>, void>({
            query: () => ({
                url: `Subscription/AllFeatures`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600, // 1 hour
            providesTags: ["AllFeatures"],
        }),
        getCurrentTenantFeatures: builder.query<string[], void>({
            query: () => ({
                url: `Subscription/Features`,
                method: "GET",
            }),
            providesTags: () => [{ type: "TenantFeatures", id: "CURRENT" }],
        }),
        getRolePermissionMetadata: builder.query<RolePermissionData, void>({
            query: () => ({
                url: `${basePath}/RolePermissions`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600, // 1 hour
        }),
        getPackageSubscriptions: builder.query<PackageSubscription[], string>({
            query: (tenantId) => ({
                url: `${basePath}/${tenantId}/PackageSubscriptions`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600, // 1 hour
            providesTags: (result, error, tenantId) => [{ type: "PackageSubscription", tenantId }],
        }),
        updatePackageSubscription: builder.mutation<void, { tenantId: string; subscriptions: PackageSubscription[] }>({
            query: ({ tenantId, subscriptions }) => ({
                url: `${basePath}/${tenantId}/PackageSubscriptions`,
                method: "PUT",
                data: subscriptions,
            }),
            invalidatesTags: (result, error, { tenantId }) => [{ type: "PackageSubscription", tenantId }],
        }),
        getRateLimits: builder.query<RateLimit, string>({
            query: (tenantId) => ({
                url: `${basePath}/${tenantId}/RateLimits`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600, // 1 hour
            providesTags: (result, error, tenantId) => [{ type: "RateLimits", tenantId }],
        }),
        updateRateLimits: builder.mutation<void, { tenantId: string; rateLimits: RateLimit }>({
            query: ({ tenantId, rateLimits }) => ({
                url: `${basePath}/${tenantId}/RateLimits`,
                method: "PUT",
                data: rateLimits,
            }),
            invalidatesTags: (result, error, { tenantId }) => [{ type: "RateLimits", tenantId }],
        }),
        getTenantInvoices: builder.query<
            PagedResult<TenantInvoice>,
            { tenantId?: string; filter: TenantInvoiceFilter }
        >({
            query: ({ tenantId, filter }) => ({
                url: `${basePath}/${tenantId ? tenantId + "/" : ""}Invoices`,
                method: "GET",
                params: filter,
            }),
            keepUnusedDataFor: 3600, // 1 hour
            providesTags: (result, error, { tenantId }) => [{ type: "TenantInvoices", tenantId: tenantId || "all" }],
        }),
        generateTenantInvoices: builder.mutation<TenantInvoiceGenerationResult, { tenantId?: string; month: string }>({
            query: ({ tenantId, month }) => ({
                url: `${basePath}/${tenantId ? tenantId + "/" : ""}Invoices/Generate?month=${encodeURIComponent(month)}`,
                method: "POST",
                params: { month },
            }),
            invalidatesTags: (result, error, { tenantId }) => [{ type: "TenantInvoices", tenantId: tenantId || "all" }],
        }),
        deleteTenantInvoice: builder.mutation<void, string>({
            query: (billId) => ({
                url: `${basePath}/Invoices/${billId}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, billId) => [{ type: "TenantInvoices", tenantId: "all" }],
        }),
        sendTenantInvoiceEmail: builder.mutation<void, string>({
            query: (billId) => ({
                url: `${basePath}/Invoices/${billId}/SendEmail`,
                method: "POST",
            }),
            invalidatesTags: (result, error, billId) => [{ type: "TenantInvoices", tenantId: "all" }],
        }),
        getRevenueSummary: builder.query<RevenueSummary, void>({
            query: () => ({
                url: `${basePath}/revenueSummary`,
                method: "GET",
            }),
            keepUnusedDataFor: 300, // 5 minutes
            providesTags: () => [{ type: "RevenueSummary" }],
        }),
    }),
});

export const {
    useGetTenantsQuery,
    useGetAllTenantsQuery,
    useGetSubscribedTenantsQuery,
    useGetCurrentTenantQuery,
    useGetTenantQuery,
    useCreateTenantMutation,
    useUpdateTenantMutation,
    useUpdateCurrentTenantMutation,
    useDeleteTenantMutation,
    useGetTenantUsersQuery,
    useSetPrimaryContactMutation,
    useGetTenantFeaturesQuery,
    useUpdateTenantFeaturesMutation,
    useGetNatureOfBusinessesQuery,
    useGetAllFeaturesQuery,
    useGetCurrentTenantFeaturesQuery,
    useGetRolePermissionMetadataQuery,
    useGetPackageSubscriptionsQuery,
    useUpdatePackageSubscriptionMutation,
    useGetRateLimitsQuery,
    useUpdateRateLimitsMutation,
    useGetTenantInvoicesQuery,
    useGenerateTenantInvoicesMutation,
    useDeleteTenantInvoiceMutation,
    useSendTenantInvoiceEmailMutation,
    useGetRevenueSummaryQuery,
} = tenantApi;
